import {
  type ApolloClient,
  type NormalizedCacheObject,
  gql,
} from '@apollo/client';
import type { PANEL_STATUS, PanelFormData } from './types';
import { type PanelMember } from '../GSPanelDetail';
import { type FundingPanel } from '../../../../Nomination/components';

const GET_SINGLE_PANEL = gql`
  query ($filter: PanelBaseFilter) {
    panel(filter: $filter) {
      panelMembers {
        netId
        jobTitle
        email
        givenName
        surname
        department
        institution
        classification
        isPanelChair
      }
      fundingSource
      entryYear
      panelNo
      title
      panelRound
      panelChair {
        netId
      }
      gsHelper
      panelType
      startDate
      endDate
      status
      tagOptions
    }
  }
`;

export const useGetSinglePanel = async (
  client: ApolloClient<NormalizedCacheObject>,
  panelId: string,
): Promise<PanelFormData> => {
  const { data } = await client.query({
    query: GET_SINGLE_PANEL,
    variables: {
      filter: {
        _id: { eq: panelId },
      },
    },
    fetchPolicy: 'network-only',
  });

  const rawData = data?.panel ?? {};
  const rawPanelMember = (rawData?.panelMembers as any[]) ?? [];

  const panelChairNetId =
    (rawPanelMember as PanelMember[]).find(member => member.isPanelChair)
      ?.netId ?? '';

  const sortedPanelMembers = [...rawPanelMember]?.sort(
    (a: PanelMember, b: PanelMember) =>
      Number(b.isPanelChair) - Number(a.isPanelChair),
  );

  const panelMember: PanelMember[] = sortedPanelMembers?.map(
    (member: PanelMember, index: number) => {
      const {
        netId,
        jobTitle,
        email,
        givenName,
        surname,
        department,
        institution,
        classification,
        isPanelChair,
      } = member;

      return {
        order: index,
        netId,
        title: '', //  this field is required in input, keep it just for consistency
        name: `${surname.toUpperCase()} ${givenName}`,
        jobTitle,
        email,
        givenName,
        surname,
        department,
        institution,
        classification,
        isPanelChair,
      };
    },
  );

  const panelFormData: PanelFormData = {
    _id: panelId,
    title: rawData?.title ?? '',
    fundingSource: rawData?.fundingSource ?? '',
    entryYear: rawData?.entryYear ?? '',
    panelType: (rawData?.panelType as FundingPanel) ?? '',
    endDate: rawData?.endDate ?? null,
    panelChair: panelChairNetId,
    tagOptions: rawData?.tagOptions ?? [],
    panelMember,
    status: rawData?.status as PANEL_STATUS,
    resultOptions: [], // not used in GSManagement
  };

  return panelFormData;
};
